// ForgeTraining — Stages 9–26: recruit training deep-dive.
// Replaces/enhances RecruitTraining.tsx with fuller analytics:
// module funnel, individual progress cards, heatmap, and duration analysis.

import { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useRecruitTraining } from "../../hooks/useForge";
import type { RecruitTrainingRow } from "../../hooks/useForge";
import { useAggregates } from "../../hooks/useDashboard";
import KPICard from "../../components/dashboard/KPICard";
import ChartCard from "../../components/dashboard/ChartCard";
import HeatmapChart from "../../components/dashboard/HeatmapChart";
import type { HeatmapCell } from "../../components/dashboard/HeatmapChart";
import StatusBadge from "../../components/dashboard/StatusBadge";
import { LiveIndicator } from "../../components/shared/LiveIndicator";
import DateRangeToggle, {
  useSinceDate,
  isAfterSince,
} from "../../components/dashboard/DateRangeToggle";
import type { DateRange } from "../../components/dashboard/DateRangeToggle";
import { useTheme } from "../../hooks/useTheme";
import { classifyByDob } from "../../lib/personas";
import { PERSONAS } from "../../lib/personas";
import type { Persona } from "../../lib/personas";

// ── Module definitions ────────────────────────────────────────────────────────

interface ModuleDef {
  key: string;
  label: string;
  seq: number; // Training sequence order
}

const MODULES: ModuleDef[] = [
  { key: "date_orientation", label: "Orientation", seq: 1 },
  { key: "date_intro_to_training", label: "Intro to Training", seq: 2 },
  { key: "date_safety", label: "Safety", seq: 3 },
  { key: "date_cpr_hcp", label: "CPR/HCP", seq: 4 },
  { key: "date_ics_100", label: "ICS-100", seq: 5 },
  { key: "date_ics_200", label: "ICS-200", seq: 6 },
  { key: "date_ics_700", label: "ICS-700", seq: 7 },
  { key: "date_ics_800", label: "ICS-800", seq: 8 },
  { key: "date_hipaa", label: "HIPAA", seq: 9 },
  { key: "date_infection_control", label: "Infection Control", seq: 10 },
  { key: "date_stress_management", label: "Stress Mgmt", seq: 11 },
  { key: "date_loda", label: "LODA", seq: 12 },
  { key: "date_work_behavior", label: "Workplace Behavior", seq: 13 },
  { key: "date_ems_ops", label: "EMS Ops", seq: 14 },
  { key: "date_suppression_ops", label: "Suppression Ops", seq: 15 },
  { key: "date_scba", label: "SCBA", seq: 16 },
  { key: "date_hazmat", label: "HAZMAT", seq: 17 },
  { key: "date_jr_member", label: "Jr Member", seq: 18 },
];

// Sorted by logical training sequence
const MODULES_SORTED = [...MODULES].sort((a, b) => a.seq - b.seq);
const _MODULE_KEYS = MODULES_SORTED.map((m) => m.key);
const MODULE_COLS = MODULES_SORTED.map((m) => m.label);
const TOTAL_MODULES = MODULES.length; // 18

// ── Helpers ───────────────────────────────────────────────────────────────────

function asNumber(v: unknown, fallback = 0): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

function asString(v: unknown, fallback = ""): string {
  if (typeof v === "string") return v;
  return fallback;
}

function recruitName(row: RecruitTrainingRow): string {
  const m = asString((row as Record<string, unknown>)["member"]);
  return m || asString(row.airtable_id) || "Unknown";
}

function isActive(row: RecruitTrainingRow): boolean {
  const s = asString(row.status).toLowerCase();
  return s === "new" || s === "in-process" || s === "in process";
}

function isCompleted(row: RecruitTrainingRow): boolean {
  const s = asString(row.status).toLowerCase();
  return s === "released for assignment";
}

function daysInTraining(row: RecruitTrainingRow): number {
  return asNumber(row.kpi_days_in_recruit_training);
}

function moduleCount(row: RecruitTrainingRow): number {
  return MODULES_SORTED.filter(
    (m) => asString((row as Record<string, unknown>)[m.key]).length > 0,
  ).length;
}

function getPersona(row: RecruitTrainingRow): Persona {
  const dob = asString(
    (row as Record<string, unknown>)["dob"] ??
      (row as Record<string, unknown>)["date_of_birth"] ??
      (row as Record<string, unknown>)["birth_date"],
  );
  return classifyByDob(dob || null);
}

// RefreshDot removed — using shared LiveIndicator component

// ── Module Completion Funnel ──────────────────────────────────────────────────

interface ModuleFunnelPoint {
  label: string;
  completed: number;
  dropOff: number;
  seq: number;
}

function buildModuleFunnel(
  activeRows: RecruitTrainingRow[],
): ModuleFunnelPoint[] {
  if (activeRows.length === 0) return [];
  return MODULES_SORTED.map((mod, i) => {
    const completed = activeRows.filter(
      (r) => asString((r as Record<string, unknown>)[mod.key]).length > 0,
    ).length;
    const prevCompleted =
      i === 0
        ? activeRows.length
        : activeRows.filter(
            (r) =>
              asString(
                (r as Record<string, unknown>)[MODULES_SORTED[i - 1].key],
              ).length > 0,
          ).length;
    return {
      label: mod.label,
      completed,
      dropOff: Math.max(0, prevCompleted - completed),
      seq: mod.seq,
    };
  });
}

interface ModuleFunnelTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: ModuleFunnelPoint }>;
}

function ModuleFunnelTooltip({ active, payload }: ModuleFunnelTooltipProps) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="glass rounded-lg px-3 py-2.5 text-xs shadow-lg min-w-[150px]">
      <p className="font-semibold text-theme-primary mb-1">{d.label}</p>
      <div className="flex justify-between gap-4">
        <span className="text-theme-muted">Completed</span>
        <span className="font-bold tabular-nums text-theme-primary">
          {d.completed}
        </span>
      </div>
      {d.dropOff > 0 && (
        <div className="flex justify-between gap-4 mt-0.5">
          <span className="text-theme-muted">Not started yet</span>
          <span className="font-bold tabular-nums text-status-amber">
            {d.dropOff}
          </span>
        </div>
      )}
    </div>
  );
}

// ── Individual Recruit Card ───────────────────────────────────────────────────

function RecruitCard({ row }: { row: RecruitTrainingRow }) {
  const [expanded, setExpanded] = useState(false);
  const name = recruitName(row);
  const days = daysInTraining(row);
  const completed = moduleCount(row);
  const pct = Math.round((completed / TOTAL_MODULES) * 100);
  const persona = getPersona(row);
  const personaProfile = PERSONAS[persona];

  const progressColor =
    pct >= 80 ? "bg-green-500" : pct >= 50 ? "bg-amber-500" : "bg-red-500";
  const isStalled = days > 90;
  const isAtRisk = days > 60 && pct < 50;

  return (
    <div
      className={`glass rounded-xl overflow-hidden ${isAtRisk ? "ring-1 ring-red-500/30" : ""}`}
    >
      <button
        type="button"
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-theme-overlay transition-colors text-left"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
      >
        {/* Chevron */}
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`shrink-0 text-theme-muted transition-transform duration-200 ${expanded ? "rotate-90" : ""}`}
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>

        {/* Name */}
        <span className="text-sm font-semibold text-theme-primary flex-1 truncate min-w-0">
          {name}
        </span>

        {/* Persona tag */}
        {persona !== "unknown" && (
          <span
            className="shrink-0 text-xs font-medium px-2 py-0.5 rounded-full hidden sm:inline-flex"
            style={{
              backgroundColor: `${personaProfile.color}18`,
              color: personaProfile.color,
            }}
          >
            {personaProfile.label.split(" ")[0]}
          </span>
        )}

        {/* At-risk flag */}
        {isAtRisk && (
          <span className="shrink-0 text-xs font-bold px-2 py-0.5 rounded-full bg-red-500/15 text-red-700 dark:text-red-400">
            At Risk
          </span>
        )}

        {/* Days badge */}
        <span
          className={`shrink-0 text-xs font-bold tabular-nums px-2 py-0.5 rounded-full ${
            isStalled
              ? "bg-red-500/15 text-red-700 dark:text-red-400"
              : days > 60
                ? "bg-amber-500/15 text-amber-700 dark:text-amber-400"
                : "bg-theme-overlay text-theme-muted"
          }`}
        >
          {days > 0 ? `${days}d` : "—"}
        </span>

        {/* Progress fraction */}
        <span className="shrink-0 text-xs text-theme-muted tabular-nums w-10 text-right">
          {completed}/{TOTAL_MODULES}
        </span>

        {/* Mini progress bar */}
        <div className="shrink-0 w-20 h-2 rounded-full bg-theme-overlay overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-300 ${progressColor}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </button>

      {/* Expanded module checklist */}
      {expanded && (
        <div className="border-t border-theme-subtle px-4 py-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
            {MODULES_SORTED.map((mod) => {
              const dateVal = asString(
                (row as Record<string, unknown>)[mod.key],
              );
              const done = dateVal.length > 0;
              return (
                <div
                  key={mod.key}
                  className="flex items-center justify-between gap-2 py-1 border-b border-theme-subtle/50 last:border-0"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {done ? (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="shrink-0 text-status-green"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    ) : (
                      <div className="shrink-0 w-3.5 h-3.5 rounded-full border border-theme-subtle" />
                    )}
                    <span
                      className={`text-xs truncate ${done ? "text-theme-primary" : "text-theme-muted"}`}
                    >
                      {mod.seq}. {mod.label}
                    </span>
                  </div>
                  <span className="shrink-0 text-xs text-theme-muted tabular-nums">
                    {done ? dateVal : "Not started"}
                  </span>
                </div>
              );
            })}
          </div>
          {isAtRisk && (
            <p className="mt-3 text-xs text-red-600 dark:text-red-400 font-medium">
              At Risk: {days} days in training with only {pct}% complete.
              Recommended action: Schedule a one-on-one check-in and identify
              blockers.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Duration Histogram ────────────────────────────────────────────────────────

interface HistoBucket {
  label: string;
  count: number;
  minDays: number;
  maxDays: number;
}

const HISTO_BUCKETS: HistoBucket[] = [
  { label: "0–30d", count: 0, minDays: 0, maxDays: 30 },
  { label: "30–60d", count: 0, minDays: 30, maxDays: 60 },
  { label: "60–90d", count: 0, minDays: 60, maxDays: 90 },
  { label: "90–120d", count: 0, minDays: 90, maxDays: 120 },
  { label: "120–180d", count: 0, minDays: 120, maxDays: 180 },
  { label: "180d+", count: 0, minDays: 180, maxDays: Infinity },
];

function buildHistogram(completedRows: RecruitTrainingRow[]): HistoBucket[] {
  const buckets = HISTO_BUCKETS.map((b) => ({ ...b, count: 0 }));
  for (const row of completedRows) {
    const days = daysInTraining(row);
    if (days <= 0) continue;
    for (const bucket of buckets) {
      if (days > bucket.minDays && days <= bucket.maxDays) {
        bucket.count += 1;
        break;
      }
    }
  }
  return buckets;
}

function computePercentiles(completedRows: RecruitTrainingRow[]) {
  const vals = completedRows
    .map(daysInTraining)
    .filter((v) => v > 0)
    .sort((a, b) => a - b);
  if (vals.length === 0) return { median: null, p75: null, mean: null };
  const median = vals[Math.floor(vals.length / 2)];
  const p75 = vals[Math.floor(vals.length * 0.75)];
  const mean = Math.round(vals.reduce((s, v) => s + v, 0) / vals.length);
  return { median, p75, mean };
}

interface HistoTooltipPayload {
  value: number;
  payload: HistoBucket;
}

function HistoTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: HistoTooltipPayload[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const isProblem = d.minDays >= 180;
  return (
    <div className="glass rounded-lg px-3 py-2.5 text-xs shadow-lg min-w-[140px]">
      <p className="font-semibold text-theme-primary mb-1">{label}</p>
      <div className="flex justify-between gap-4">
        <span className="text-theme-muted">Recruits</span>
        <span className="font-bold tabular-nums text-theme-primary">
          {d.count}
        </span>
      </div>
      {isProblem && d.count > 0 && (
        <p className="text-red-600 dark:text-red-400 mt-1">
          Extended — review needed
        </p>
      )}
    </div>
  );
}

function DurationAnalysis({
  completedRows,
  isLoading,
}: {
  completedRows: RecruitTrainingRow[];
  isLoading: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const axisColor = isDark ? "#9ca3af" : "#6b7280";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";

  const histogramData = useMemo(
    () => buildHistogram(completedRows),
    [completedRows],
  );
  const { median, p75, mean } = useMemo(
    () => computePercentiles(completedRows),
    [completedRows],
  );

  if (isLoading) {
    return <div className="skeleton h-52 rounded-xl" />;
  }

  const hasData = histogramData.some((b) => b.count > 0);

  return (
    <div className="space-y-4">
      {/* Percentile stats */}
      {hasData && (
        <div className="flex flex-wrap gap-3">
          {[
            { label: "Median", value: median, target: 90 },
            { label: "75th Pct", value: p75, target: 150 },
            { label: "Mean", value: mean, target: 120 },
          ].map(({ label, value, target }) => (
            <div
              key={label}
              className="glass rounded-xl px-3 py-2.5 flex flex-col gap-0.5"
            >
              <span className="text-xs text-theme-muted">{label}</span>
              <span
                className={`text-xl font-bold tabular-nums ${
                  value === null
                    ? "text-theme-muted"
                    : value <= target
                      ? "text-status-green"
                      : value <= target * 1.5
                        ? "text-status-amber"
                        : "text-status-red"
                }`}
              >
                {value !== null ? `${value}d` : "—"}
              </span>
            </div>
          ))}
          <div className="glass rounded-xl px-3 py-2.5 flex flex-col gap-0.5">
            <span className="text-xs text-theme-muted">Benchmark</span>
            <span className="text-xs text-theme-secondary mt-0.5">
              90–180d normal
            </span>
            <span className="text-xs text-status-red">180d+ is a problem</span>
          </div>
        </div>
      )}

      {!hasData ? (
        <div className="flex flex-col items-center justify-center h-40 gap-2">
          <p className="text-sm text-theme-muted">No completed recruit data</p>
          <p className="text-xs text-theme-muted opacity-60">
            Duration distribution appears once recruits graduate
          </p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <BarChart
            data={histogramData}
            margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
          >
            <CartesianGrid
              vertical={false}
              stroke={gridColor}
              strokeDasharray="3 3"
            />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: axisColor }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: axisColor }}
              tickLine={false}
              axisLine={false}
              width={32}
              allowDecimals={false}
            />
            <Tooltip
              content={<HistoTooltip />}
              cursor={{
                fill: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
              }}
            />
            <Bar
              dataKey="count"
              name="Recruits"
              radius={[4, 4, 0, 0]}
              maxBarSize={64}
            >
              {histogramData.map((entry, i) => {
                const color =
                  entry.maxDays <= 60
                    ? "#16a34a"
                    : entry.maxDays <= 120
                      ? "#f59e0b"
                      : "#dc2626";
                return (
                  <Cell key={`cell-${i}`} fill={color} fillOpacity={0.85} />
                );
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ForgeTraining() {
  const INTERVAL = 120_000;

  const [range, setRange] = useState<DateRange>("all");
  const sinceDate = useSinceDate(range);

  const {
    data: trainingData,
    isLoading: trainingLoading,
    lastUpdatedAt,
  } = useRecruitTraining(INTERVAL);
  const { data: aggregatesData, isLoading: aggLoading } =
    useAggregates(INTERVAL);

  const allRows = trainingData?.data ?? [];
  const agg = aggregatesData?.aggregates ?? {};

  // ── Date range filter ────────────────────────────────────────────────────────
  const rows = useMemo(() => {
    if (!sinceDate) return allRows;
    return allRows.filter((r) => {
      const orientation = asString(
        (r as Record<string, unknown>)["date_orientation"],
      );
      const created = asString(
        (r as Record<string, unknown>)["created_at"] ?? "",
      );
      return isAfterSince(orientation || created, sinceDate);
    });
  }, [allRows, sinceDate]);

  const activeRows = useMemo(() => rows.filter(isActive), [rows]);
  const completedRows = useMemo(() => rows.filter(isCompleted), [rows]);

  // ── KPI values ────────────────────────────────────────────────────────────────
  const activeCount = activeRows.length;
  const completedCount = completedRows.length;

  const stalledCount = useMemo(
    () => activeRows.filter((r) => daysInTraining(r) > 90).length,
    [activeRows],
  );

  const avgDays = useMemo(() => {
    const fromAgg = asNumber(agg["avg_days_in_recruit_training"]);
    if (fromAgg > 0) return Math.round(fromAgg);
    const vals = completedRows.map(daysInTraining).filter((v) => v > 0);
    if (vals.length === 0) return 0;
    return Math.round(vals.reduce((s, v) => s + v, 0) / vals.length);
  }, [agg, completedRows]);

  const stalledAccent =
    stalledCount === 0 ? "green" : stalledCount <= 2 ? "amber" : "red";
  const avgAccent =
    avgDays === 0
      ? "neutral"
      : avgDays <= 90
        ? "green"
        : avgDays <= 150
          ? "amber"
          : "red";

  // ── Section 1: Module Funnel data ─────────────────────────────────────────────
  const moduleFunnelData = useMemo(
    () => buildModuleFunnel(activeRows),
    [activeRows],
  );

  // ── Section 3: Heatmap data ───────────────────────────────────────────────────
  const heatmapCells = useMemo<HeatmapCell[]>(() => {
    return activeRows.flatMap((row) => {
      const name = recruitName(row);
      const days = daysInTraining(row);
      return MODULES_SORTED.map((mod, i) => {
        const rawDate = asString((row as Record<string, unknown>)[mod.key]);
        const hasDate = rawDate.trim().length > 0;
        let value: HeatmapCell["value"];
        if (hasDate) {
          value = "complete";
        } else if (days > 60) {
          value = "overdue";
        } else {
          value = "not_started";
        }
        return {
          row: name,
          col: MODULE_COLS[i],
          value,
          date: hasDate ? rawDate : undefined,
        };
      });
    });
  }, [activeRows]);

  const heatmapRows = useMemo(
    () =>
      [...activeRows]
        .sort((a, b) => daysInTraining(b) - daysInTraining(a))
        .map(recruitName),
    [activeRows],
  );

  // ── Individual cards sorted ───────────────────────────────────────────────────
  const sortedActive = useMemo(
    () => [...activeRows].sort((a, b) => daysInTraining(b) - daysInTraining(a)),
    [activeRows],
  );

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 md:space-y-8 overflow-x-hidden">
      {/* ── Page header ──────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-theme-primary">
            Recruit Training
          </h1>
          <p className="text-theme-secondary mt-1 text-sm">
            Stages 9–26 — 18-module training pipeline. Module completion,
            individual progress, heatmap, and duration analysis.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <LiveIndicator lastUpdatedAt={lastUpdatedAt} />
        </div>
      </div>

      {/* ── Date Range Toggle ─────────────────────────────────────────── */}
      <DateRangeToggle value={range} onChange={setRange} />

      {/* ── KPI Row ───────────────────────────────────────────────────── */}
      <section>
        <h2 className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-4">
          Training Overview
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <KPICard
            label="Active Recruits"
            value={trainingLoading ? "—" : activeCount}
            subtitle="status: New or In-Process"
            accent="neutral"
            isLoading={trainingLoading}
          />

          <KPICard
            label="Completed"
            value={trainingLoading ? "—" : completedCount}
            subtitle="released for assignment"
            accent={completedCount > 0 ? "green" : "neutral"}
            isLoading={trainingLoading}
            trend={
              !trainingLoading && completedCount > 0
                ? {
                    direction: "up",
                    label: "Graduated",
                    positiveIsGood: true,
                  }
                : undefined
            }
          />

          <KPICard
            label="Stalled"
            value={trainingLoading ? "—" : stalledCount}
            subtitle={
              stalledCount === 0
                ? "None over 90 days"
                : "active recruits >90 days"
            }
            accent={stalledAccent}
            isLoading={trainingLoading}
            trend={
              !trainingLoading
                ? {
                    direction: stalledCount === 0 ? "down" : "up",
                    label:
                      stalledCount === 0
                        ? "None stalled"
                        : `${stalledCount} over 90d`,
                    positiveIsGood: false,
                  }
                : undefined
            }
          />

          <KPICard
            label="Avg Training Days"
            value={
              aggLoading && trainingLoading
                ? "—"
                : avgDays === 0
                  ? "No data"
                  : avgDays
            }
            subtitle="benchmark: 90–180 days"
            accent={avgAccent}
            isLoading={aggLoading && trainingLoading}
            trend={
              !aggLoading && !trainingLoading && avgDays > 0
                ? {
                    direction: avgDays <= 90 ? "down" : "up",
                    label:
                      avgDays <= 90
                        ? "Fast track"
                        : avgDays <= 180
                          ? "Normal range"
                          : "Over benchmark",
                    positiveIsGood: false,
                  }
                : undefined
            }
          />
        </div>
      </section>

      {/* ── Section 1: Module Completion Funnel ──────────────────────── */}
      <section>
        <ChartCard
          title="Training Module Completion"
          subtitle="Count of active recruits who completed each module, in training sequence order"
          headerRight={
            trainingLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : activeCount > 0 ? (
              <StatusBadge
                label={`${activeCount} active recruits`}
                variant="blue"
              />
            ) : (
              <StatusBadge label="No active recruits" variant="muted" />
            )
          }
          minHeight={260}
        >
          {trainingLoading ? (
            <div className="skeleton h-52 rounded-xl" />
          ) : moduleFunnelData.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 gap-2">
              <p className="text-sm text-theme-muted">
                No active recruits in training
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart
                  data={moduleFunnelData}
                  margin={{ top: 8, right: 8, left: -10, bottom: 8 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(128,128,128,0.15)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 9, fill: "var(--color-theme-muted)" }}
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    angle={-35}
                    textAnchor="end"
                    height={56}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "var(--color-theme-muted)" }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip content={<ModuleFunnelTooltip />} />
                  <Bar
                    dataKey="completed"
                    fill="#0E403A"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={40}
                    fillOpacity={0.8}
                  />
                </BarChart>
              </ResponsiveContainer>

              {/* Module sequence table */}
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-theme-subtle">
                      <th className="px-3 py-2 text-left text-theme-muted font-semibold uppercase tracking-wider w-6">
                        #
                      </th>
                      <th className="px-3 py-2 text-left text-theme-muted font-semibold uppercase tracking-wider">
                        Module
                      </th>
                      <th className="px-3 py-2 text-right text-theme-muted font-semibold uppercase tracking-wider">
                        Completed
                      </th>
                      <th className="px-3 py-2 text-right text-theme-muted font-semibold uppercase tracking-wider">
                        Not Yet
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {moduleFunnelData.map((m) => (
                      <tr
                        key={m.label}
                        className="hover:bg-theme-overlay/50 transition-colors"
                      >
                        <td className="px-3 py-2 text-theme-muted tabular-nums">
                          {m.seq}
                        </td>
                        <td className="px-3 py-2 font-medium text-theme-primary">
                          {m.label}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-status-green">
                          {m.completed}
                        </td>
                        <td
                          className={`px-3 py-2 text-right tabular-nums ${m.dropOff > 0 ? "text-status-amber" : "text-theme-muted"}`}
                        >
                          {m.dropOff > 0 ? m.dropOff : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </ChartCard>
      </section>

      {/* ── Section 2: Individual Recruit Progress ────────────────────── */}
      <section>
        <ChartCard
          title="Individual Recruit Progress"
          subtitle="Active recruits sorted by days in training — longest first. Expand for full module checklist."
          headerRight={
            !trainingLoading && activeCount > 0 ? (
              <StatusBadge label={`${activeCount} active`} variant="blue" />
            ) : !trainingLoading ? (
              <StatusBadge label="No active recruits" variant="muted" />
            ) : null
          }
          minHeight={0}
        >
          {trainingLoading ? (
            <div className="space-y-2 p-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="skeleton h-11 rounded-xl" />
              ))}
            </div>
          ) : sortedActive.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2 text-center">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-status-green"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
              <p className="text-sm font-medium text-status-green">
                No active recruits in pipeline
              </p>
              <p className="text-xs text-theme-muted opacity-70">
                Recruit cards appear once training records sync
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {sortedActive.map((row, idx) => (
                <RecruitCard
                  key={row.airtable_id || `recruit-${idx}`}
                  row={row}
                />
              ))}
            </div>
          )}
        </ChartCard>
      </section>

      {/* ── Section 3: Module Completion Heatmap ─────────────────────── */}
      <section>
        <ChartCard
          title="Module Completion Heatmap"
          subtitle="Rows: recruits sorted by days descending. Columns: 18 modules in training order. Green = done, red = overdue."
          headerRight={
            trainingLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : activeCount > 0 ? (
              <StatusBadge label={`${activeCount} recruits`} variant="blue" />
            ) : (
              <StatusBadge label="No active recruits" variant="muted" />
            )
          }
          minHeight={
            heatmapRows.length > 0 ? heatmapRows.length * 36 + 100 : 160
          }
        >
          <HeatmapChart
            cells={heatmapCells}
            rows={heatmapRows}
            cols={MODULE_COLS}
            isLoading={trainingLoading}
          />
        </ChartCard>
      </section>

      {/* ── Section 4: Training Duration Analysis ────────────────────── */}
      <section>
        <ChartCard
          title="Training Duration Analysis"
          subtitle="Completed recruits by days in training — green <60d, amber 60–120d, red >120d. Target: 90–180 days, over 180 is a problem."
          headerRight={
            !trainingLoading ? (
              <StatusBadge
                label={`${completedCount} completed`}
                variant="muted"
              />
            ) : null
          }
          minHeight={260}
        >
          <DurationAnalysis
            completedRows={completedRows}
            isLoading={trainingLoading}
          />
        </ChartCard>
      </section>
    </div>
  );
}
